import React, { useState } from 'react';
import { Task } from '../utils/types';
import { Check, X, MessageSquare, Clock, CheckCircle, XCircle, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
interface TaskValidationProps {
  tasks: Task[];
  onValidate: (taskId: string, status: 'approved' | 'rejected', comment?: string) => void;
}
export function TaskValidation({
  tasks,
  onValidate
}: TaskValidationProps) {
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [validating, setValidating] = useState(false);
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const pendingTasks = tasks.filter(task => task.validationStatus === 'pending');
  if (pendingTasks.length === 0) {
    return <div className="text-center py-8 bg-green-50 rounded-xl border border-green-200" data-id="element-189">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-190">
          <CheckCircle className="w-6 h-6 text-green-600" data-id="element-191" />
        </div>
        <p className="text-green-800 font-medium" data-id="element-192">
          Toutes les tâches sont validées
        </p>
        <p className="text-green-600 text-sm mt-1" data-id="element-193">
          Aucune validation en attente
        </p>
      </div>;
  }
  const handleValidate = async (taskId: string, status: 'approved' | 'rejected') => {
    setValidating(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    onValidate(taskId, status, comment);
    setComment('');
    setSelectedTask(null);
    setExpandedTask(null);
    setValidating(false);
  };
  return <div className="space-y-4" data-id="element-194">
      <div className="flex items-center justify-between" data-id="element-195">
        <div className="flex items-center gap-2" data-id="element-196">
          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center" data-id="element-197">
            <Clock className="w-4 h-4 text-orange-600" data-id="element-198" />
          </div>
          <div data-id="element-199">
            <h3 className="font-semibold text-gray-900" data-id="element-200">
              Tâches en attente de validation
            </h3>
            <p className="text-sm text-gray-500" data-id="element-201">
              {pendingTasks.length} tâche{pendingTasks.length > 1 ? 's' : ''} à
              valider
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3" data-id="element-202">
        {pendingTasks.map((task, index) => <motion.div key={task.id} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.1
      }} className="bg-white border-2 border-orange-200 rounded-xl overflow-hidden" data-id="element-203">
            {/* Task Header */}
            <div className="p-4" data-id="element-204">
              <div className="flex items-start justify-between gap-3 mb-3" data-id="element-205">
                <div className="flex-1 min-w-0" data-id="element-206">
                  <h4 className="font-bold text-gray-900 mb-1" data-id="element-207">{task.title}</h4>
                  <p className="text-sm text-gray-600 mb-2" data-id="element-208">
                    {task.description}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-209">
                    <span data-id="element-210">Par {task.createdBy}</span>
                    <span data-id="element-211">•</span>
                    <span data-id="element-212">
                      {new Date(task.createdAt).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
                <span className="px-2 py-1 bg-orange-500 text-white text-xs font-medium rounded-full flex-shrink-0" data-id="element-213">
                  En attente
                </span>
              </div>

              {/* Images Preview */}
              <button onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)} className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-214">
                <div className="flex items-center gap-2" data-id="element-215">
                  <ImageIcon size={16} className="text-gray-600" data-id="element-216" />
                  <span className="text-sm font-medium text-gray-900" data-id="element-217">
                    {task.images.length} photo
                    {task.images.length > 1 ? 's' : ''} jointe
                    {task.images.length > 1 ? 's' : ''}
                  </span>
                </div>
                <motion.div animate={{
              rotate: expandedTask === task.id ? 180 : 0
            }} transition={{
              duration: 0.2
            }} data-id="element-218">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-gray-600" data-id="element-219">
                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-220" />
                  </svg>
                </motion.div>
              </button>

              {/* Expanded Images */}
              <AnimatePresence data-id="element-221">
                {expandedTask === task.id && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="overflow-hidden" data-id="element-222">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3" data-id="element-223">
                      {task.images.map(image => <div key={image.id} className="aspect-square rounded-lg overflow-hidden bg-gray-100" data-id="element-224">
                          <img src={image.url} alt={image.description} className="w-full h-full object-cover" data-id="element-225" />
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Validation Actions */}
              <AnimatePresence data-id="element-226">
                {selectedTask === task.id && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="overflow-hidden" data-id="element-227">
                    <div className="pt-4 mt-4 border-t border-gray-200" data-id="element-228">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-229">
                        Commentaire (optionnel)
                      </label>
                      <textarea value={comment} onChange={e => setComment(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F59E0B] focus:border-transparent outline-none text-sm" rows={3} placeholder="Ajouter un commentaire sur la tâche réalisée..." data-id="element-230" />
                    </div>
                  </motion.div>}
              </AnimatePresence>

              <div className="flex gap-2 mt-4" data-id="element-231">
                {selectedTask === task.id ? <>
                    <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" leftIcon={<Check size={16} data-id="element-233" />} onClick={() => handleValidate(task.id, 'approved')} isLoading={validating} data-id="element-232">
                      Approuver la tâche
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50" leftIcon={<X size={16} data-id="element-235" />} onClick={() => handleValidate(task.id, 'rejected')} isLoading={validating} data-id="element-234">
                      Rejeter
                    </Button>
                  </> : <Button size="sm" variant="outline" className="w-full" leftIcon={<MessageSquare size={16} data-id="element-237" />} onClick={() => setSelectedTask(task.id)} data-id="element-236">
                    Valider cette tâche
                  </Button>}
              </div>
            </div>
          </motion.div>)}
      </div>
    </div>;
}